import { Injectable } from '@angular/core';

import { map,  switchMap } from 'rxjs/operators';
import { Observable, of} from 'rxjs';

import { TargetingSearchesApiService } from '../../../api/targeting/targeting-searches.api.service';
import { TargetingListsApiService } from '../../../api/targeting/targeting-lists.api.service';
import { OutletsPublicApi } from '../../../api/targeting/outlets-public.api';
import { EntityIdApiModel } from '../../../api/targeting/model/entity-id-api-model';
import { TargetingSearchResponseApiModel } from '../../../api/targeting/model/targeting-search-response.api.model';

import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';

import { TargetingSearch } from '../../../Discover/Targeting/models/targeting-search.model';
import { JournodexEnumsService } from '../../../Discover/Core/services/journodex-enums.service';
import { BaseSearchService, TargetingFacetCollection } from '../../../Discover/Targeting/services/base-search.service';
import { TargetingList } from '../../../Discover/Targeting/models/targeting-list.model';
import { TargetingResultFactory } from '../../../Discover/Targeting/factories/targeting-result.factory';

import { JournodexRegionsService } from '../../../Common/Journodex/services/journodex-regions.service';
import { OutletContactTabType, TargetingRhpContextsService } from '../../../Common/TargetingShared/services/targeting-rhp-contexts.service';
import { FilterTypeModel } from '../../../Common/Filter/models/abstract-filter-type.model';
import { JournodexSubjectsService } from '../../../Common/Journodex/services/journodex-subjects.service';
import { SearchModeEnum } from '../../../Common/TargetingShared/enum/search-mode.enum';
import { DistributionApi } from '@api/distributions/distribution.api';
import { LocalizedEnum } from '@api/journodex/model/localized-enum';
import { SortOrderEnum } from '@api/shared/enums/sort-order.enum';
import { OUTLET_SORT_FIELDS } from '@Common/TargetingShared/model/outlet-sort-fields.const';


@Injectable()
export class AffiliatesService extends BaseSearchService {

    outletId: string;
    affiliatesTabVisible: boolean = false;

    affiliateSortFields: LocalizedEnum[] = OUTLET_SORT_FIELDS;

    constructor(
        protected targetingSearchesApiService: TargetingSearchesApiService,
        protected targetingListsApiService: TargetingListsApiService,
        protected subjectsService: JournodexSubjectsService,
        protected journodexEnumsService: JournodexEnumsService,
        protected journodexRegionService: JournodexRegionsService,
        protected localizationService: LocalizationService,
        protected targetingRhpContextsService: TargetingRhpContextsService,
        protected outletsApi: OutletsPublicApi,
        protected targetingResultFactory: TargetingResultFactory,
        protected currentUserStateService: CurrentUserStateService,
        protected distributionApiService: DistributionApi,
    ) {
        super(
            targetingSearchesApiService,
            targetingListsApiService,
            subjectsService,
            journodexEnumsService,
            journodexRegionService,
            localizationService,
            targetingResultFactory,
            currentUserStateService,
            distributionApiService
        );
        this.setFilters(this.allFilters);

        this.subscriptions.push(
            this.targetingRhpContextsService.getOutletId().subscribe(outletId => {
                if (outletId && this.outletId !== outletId) {
                    this.outletId = outletId;
                    if (this.affiliatesTabVisible) {
                        this.clearFilters();
                        this.doSearch(true, true);
                    }
                }
            }),
            this.targetingRhpContextsService.getCurrentOutletContactTab().subscribe(tab => {
                this.setAffiliatesTabVisible(tab === OutletContactTabType.affiliates);
            })
        );

        //Send the list of sort fields for affiliates
        this.targetingSortFields$.next(this.affiliateSortFields);

        // Set the default sort field to the first one in the affiliate sort fields array
        this.sortField = this.affiliateSortFields[0];
        // Emit the updated sort field to subscribers
        this.sortField$.next(this.sortField);

        // Set the default sort order to ascending
        this.sortOrder = SortOrderEnum.ascending;
        // Emit the updated sort order to subscribers
        this.sortOrder$.next(this.sortOrder);
    }

    getFilterDefinitions(): FilterTypeModel[] {
        const filters: FilterTypeModel[] = [
            this.audienceReachFilter,
            this.subjectFilter,
            this.languageFilter,
            this.locationFilter,
            this.contactNameFilter,
            this.outletNameFilter,
            this.targetAudienceRegionFilter
        ];
        // TODO: add aoc and list filter, alphabetically. Backend not implemented at the moment

        return filters;
    }

    setAffiliatesTabVisible(visible: boolean) {
        if (this.affiliatesTabVisible !== visible) {
            this.affiliatesTabVisible = visible;
            if (visible && this.outletId) {
                this.doSearch(true, true);
            }
        }
    }

    canSetPageCriteria(): Observable<boolean> {
        return of(false);
    }

    public doSearch(refreshPrimaryFacets: boolean = false, refreshFilteredFacets: boolean = false): Observable<TargetingSearchResponseApiModel> {

        if (this.mainSearchRequest) {
            this.mainSearchRequest.unsubscribe();
            this.mainSearchRequest = null;
        }

        const search = new TargetingSearch();
        search.searchMode = SearchModeEnum.outlet;
        let filters: TargetingSearch;
        const isFiltered: boolean = this.hasFiltersToApply();

        if (isFiltered) {
            filters = this.generateFiltersSearch();
        }

        const mainSearchRequest: Observable<TargetingSearchResponseApiModel> = this.fastSearch(search, filters);

        // just to make sure it runs, in case the caller doesn't subscribe
        this.searchLoading$.next(true);
        this.mainSearchRequest = mainSearchRequest.subscribe({
            next: () => {
                this.mainSearchRequest = null;
                this.fetchListMemberships();
                // this.setSearchState(SearchStatesEnum.success);
            },
            error: (err) => {
                console.log(err);
                // this.setSearchState(SearchStatesEnum.error, err);
            }
        });

        let filteredFacetRequest: Observable<TargetingFacetCollection>;
        if (refreshFilteredFacets) {
            if (this.filteredFacetRequest) {
                this.filteredFacetRequest.unsubscribe();
            }
            filteredFacetRequest = this.facetSearch(search, filters);

            this.filteredFacetRequest = filteredFacetRequest.subscribe(collection => {
                this.setFilteredFacets(collection);
                this.filteredFacetRequest = null;
            });
        }

        let primaryFacetRequest: Observable<TargetingFacetCollection>;
        if (refreshPrimaryFacets) {
            if (this.primaryFacetRequest) {
                this.primaryFacetRequest.unsubscribe();
            }
            if (isFiltered || !filteredFacetRequest) {
                primaryFacetRequest = this.facetSearch(search);
            } else {
                primaryFacetRequest = filteredFacetRequest;
            }
            this.primaryFacetRequest = primaryFacetRequest.subscribe(collection => {
                this.setPrimaryFacets(collection);
                this.primaryFacetRequest = null;
            });
        }

        return mainSearchRequest;
    }

    // Fast search is just the results without any facets
    fastSearch(search: TargetingSearch, filters?: TargetingSearch): Observable<TargetingSearchResponseApiModel> {
        const rows = this.pageCriteria.pageSize;
        const start = (this.pageCriteria.page - 1) * rows;
        const sortOrder = this.sortOrder;
        const sortField = this.sortField ? this.sortField.enumValue : null;

        const searchRequest: Observable<TargetingSearchResponseApiModel> = this.outletsApi.searchAffiliates(
            search.toApiModel(false, filters).query,
            this.outletId,
            start,
            rows,
            sortOrder,
            sortField
        );

        return searchRequest.pipe(
            switchMap(async response => {
                await this.setSearchResults(response);
                return response;
            })
        );
    }

    facetSearch(search: TargetingSearch, filters?: TargetingSearch): Observable<TargetingFacetCollection> {
        const sortOrder = this.sortOrder;
        const sortField = this.sortField ? this.sortField.enumValue : null;

        const searchRequest: Observable<TargetingSearchResponseApiModel> = this.outletsApi.searchAffiliates(
            search.toApiModel(true, filters).query,
            this.outletId,
            0,
            0,
            sortOrder,
            sortField
        );

        return searchRequest.pipe(map(response => {
            return this.parseFacets(response.facetResults);
        }));
    }

    // We only emit the results and primary contact once this completes
    // It slows the load a little bit but simplifies the code
    fetchListMemberships() {
        // Both of these have to be done so we can get the memberships
        // for the primary contact as well as the search results at the same time
        if (this.mainSearchRequest) {
            return;
        }

        let contactsToFetch: EntityIdApiModel[] = [];

        contactsToFetch = this.results
            .map(x => {
                return <EntityIdApiModel> {
                    contactUuid : x.id
                };
            });


        this.targetingListsApiService.getListMemberships(contactsToFetch).subscribe(listMemberships => {
            for (const result of this.results) {
                result.listMemberships = listMemberships
                    .find(memberships => memberships.uuid === result.id)
                    .targetingLists.map(x => new TargetingList(x));

            }

            this.nResults$.next(this.nResults);
            this.results$.next(this.results);
            this.searchLoading$.next(false);
        });
    }
}
